import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import { DataGrid, Column, SearchPanel, Paging, FilterRow, Toolbar, Item } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react';
import { newToast } from '../../../components';
import { useScreenSize } from '../../../utils/media-query';
import RemisionesAPI from '../../../api/api.remmisions';
import { useApiOrders } from '../../../api/api.orders';
import { ETypeRemission, RemisionData } from '../../../types/types.remissions';
import moment from 'moment';
import './ModalRemision.scss';
import { ModalReportRemision } from '../../../components/library/reporting/ModalReportRemision';

interface ModalViewRemisionProps {
  visible: boolean;
  remisionId: number | null;
  onClose: () => void;
  wrapperAttr?: { class: string };
}

export const ModalViewRemision: React.FC<ModalViewRemisionProps> = ({ visible, remisionId, onClose, wrapperAttr = { class: '' } }) => {
  const [loading, setLoading] = useState(false);
  const [remisionData, setRemisionData] = useState<RemisionData | null>(null);
  const [visibleReport, setVisibleReport] = useState(false);
  const { isSmall } = useScreenSize();
  const api = RemisionesAPI();
  const { findOrder } = useApiOrders();

  const handleViewClick = (data: any) => {
    const ordId = data?.Orden?.Ord_Id;
    if (!ordId) {
      return;
    }
    setLoading(true);
    findOrder(undefined, ordId, setLoading, true).finally(() => {
      onClose();
    });
  };

  // Usar useRef para evitar dependencias innecesarias
  const loadedRemisionId = useRef<number | null>(null);

  // Mapeo de estados
  const getEstadoInfo = (estadoId: number) => {
    const estados = {
      [ETypeRemission.CREADA]: { nombre: 'CREADA', color: '#6c757d', bgColor: '#f8f9fa' },
      [ETypeRemission.IMPRESA]: { nombre: 'IMPRESA', color: '#0d6efd', bgColor: '#e7f1ff' },
      [ETypeRemission.ENVIADA]: { nombre: 'ENVIADA', color: '#fd7e14', bgColor: '#fff3e0' },
      [ETypeRemission.EN_PROCESO]: { nombre: 'EN PROCESO', color: '#ffc107', bgColor: '#fff8e1' },
      [ETypeRemission.COMPLETADA]: { nombre: 'COMPLETADA', color: '#198754', bgColor: '#e8f5e8' },
      [ETypeRemission.CANCELADA]: { nombre: 'CANCELADA', color: '#dc3545', bgColor: '#ffe6e6' },
    };
    return estados[estadoId] || { nombre: 'DESCONOCIDO', color: '#6c757d', bgColor: '#f8f9fa' };
  };

  // Cargar datos de la remisión - SIN onClose en dependencias
  const loadRemisionData = useCallback(
    async (id: number) => {
      setLoading(true);

      try {
        const data = await api.getRemisionById(id);

        if (data) {
          setRemisionData(data);
          loadedRemisionId.current = id;
        } else {
          console.error('No se recibieron datos de la remisión');
          newToast('ERROR', 'No se pudo cargar la información de la remisión', 4);
          // No llamar onClose aquí para evitar bucles
        }
      } catch (error) {
        console.error('Error cargando remisión:', error);
        newToast('ERROR', 'Error al cargar los datos de la remisión', 4);
        // No llamar onClose aquí para evitar bucles
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Manejar cambios en visible y remisionId
  useEffect(() => {
    if (visible && remisionId) {
      // Solo cargar si es diferente al que ya está cargado
      if (loadedRemisionId.current !== remisionId) {
        loadRemisionData(remisionId);
      }
    } else if (!visible) {
      // Limpiar datos cuando se cierra el modal
      setRemisionData(null);
      loadedRemisionId.current = null;
    }
  }, [visible, remisionId, loadRemisionData]);

  // Renderizar badge de urgente
  const renderUrgente = (cellData: any) => {
    return cellData.value ? (
      <span
        style={{
          backgroundColor: '#ff4444',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: 'bold',
        }}>
        URGENTE
      </span>
    ) : (
      <span>NORMAL</span>
    );
  };

  // Renderizar cantidad
  const renderCantidad = (cellData: any) => {
    const cantidad = cellData.value || 1;
    return (
      <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
        {cantidad > 1 ? (
          <span
            style={{
              backgroundColor: '#007acc',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px',
            }}>
            x{cantidad}
          </span>
        ) : (
          <span>{cantidad}</span>
        )}
      </div>
    );
  };

  // Renderizar código del estudio
  const renderEstudioCodigo = (cellData: any) => {
    return <div>{cellData.value}</div>;
  };

  // Renderizar paciente
  const renderPaciente = (cellData: any) => {
    const paciente = cellData.data.Orden?.Paciente;
    if (!paciente) return <span className='text-muted'>Sin paciente</span>;

    return (
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{`${paciente.Pc_Nombre} ${paciente.Pc_Apellido1} ${paciente.Pc_Apellido2 || ''}`.trim()}</div>
        <div style={{ fontSize: '11px', color: '#666' }}>DOC: {paciente.Pc_Identificacion}</div>
      </div>
    );
  };

  // Renderizar número de orden
  const renderOrden = (cellData: any) => {
    const numero = cellData.data.Orden?.Ord_Numero;
    const ordId = cellData.data.Orden?.Ord_Id;

    if (!numero) {
      return <div>N/A</div>;
    }

    if (!ordId) {
      return <div>{numero}</div>;
    }

    return (
      <div
        role='button'
        tabIndex={0}
        onClick={() => handleViewClick(cellData.data)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleViewClick(cellData.data);
          }
        }}
        title='Ver orden'
        style={{
          color: '#1976d2',
          cursor: 'pointer',
          fontWeight: 600,
          textDecoration: 'underline',
        }}>
        {numero}
      </div>
    );
  };

  // Renderizar área
  const renderArea = (cellData: any) => {
    const area = cellData.data.Estudio?.SysA_AreasAnalisis?.Aa_Nombre;
    return area ? <div>{area}</div> : <span className='text-muted'>Sin área</span>;
  };

  // Renderizar resultado recibido
  const renderResultadoRecibido = (cellData: any) => {
    const recibido = cellData.value;
    const fechaResultado = cellData.data.Rmd_FechaResultado;

    return (
      <div style={{ textAlign: 'center' }}>
        <span
          style={{
            backgroundColor: recibido ? '#28a745' : '#ffc107',
            color: recibido ? 'white' : '#212529',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 'bold',
          }}>
          {recibido ? 'RECIBIDO' : 'PENDIENTE'}
        </span>
        {recibido && fechaResultado && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{moment(fechaResultado).format('DD/MM/YYYY HH:mm')}</div>}
      </div>
    );
  };

  // Renderizar notas
  const renderNotas = (cellData: any) => {
    if (!cellData.value) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>Sin observaciones</span>;
    }

    const truncated = cellData.value.length > 50 ? `${cellData.value.substring(0, 50)}...` : cellData.value;

    return (
      <div title={cellData.value} style={{ fontSize: '12px' }}>
        {truncated}
      </div>
    );
  };

  const handleCloseModalPrint = useCallback(() => {
    setVisibleReport(false);
  }, []);

  // Manejar click en imprimir
  const handleClicPrint = useCallback(() => {
    if (remisionData) {
      setVisibleReport(true);
    } else {
      newToast('WARNING', 'No hay datos de remisión para imprimir', 3);
    }
  }, [remisionData]);

  // Manejar el cierre del modal
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // No mostrar nada si no está visible o no hay datos
  if (!visible || !remisionData) {
    return null;
  }

  const estadoInfo = getEstadoInfo(remisionData.Rm_Estado);

  return (
    <>
      <Popup visible={visible} onHiding={handleClose} dragEnabled={false} hideOnOutsideClick={false} showCloseButton={true} showTitle={true} title={`Remisión No. ${remisionData.Rm_Id}`} fullScreen={isSmall} wrapperAttr={{ ...wrapperAttr, class: `${wrapperAttr?.class} form-popup` }} width={isSmall ? '100%' : '90%'} height={isSmall ? '100%' : '90%'}>
        <div className='modal-view-remision' style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header con información general */}
          <div
            className='remision-header'
            style={{
              padding: '10px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
            }}>
            {/* Primera fila: Estado y fechas principales */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isSmall ? '1fr' : 'auto 1fr auto',
                gap: '10px',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#666' }}>
                <div>
                  <strong>Fecha creación:</strong> {moment(remisionData.Rm_FechaCreacion).format('DD/MM/YYYY HH:mm')}
                </div>
                {remisionData.Rm_FechaEnvio && (
                  <div>
                    <strong>Enviada:</strong> {moment(remisionData.Rm_FechaEnvio).format('DD/MM/YYYY HH:mm')}
                  </div>
                )}
              </div>
            </div>

            {/* Segunda fila: Cards de información */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isSmall ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '16px',
              }}>
              {/* Card Laboratorio */}
              <div
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: 'white',
                }}>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Laboratorio de Remisión</h5>
                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{remisionData.LaboratorioRemision?.Lr_Nombre || 'Sin laboratorio'}</div>
                {/* {remisionData.LaboratorioRemision?.Lr_Telefono && <div style={{ fontSize: '11px', color: '#666' }}>Teléfono: {remisionData.LaboratorioRemision.Lr_Telefono}</div>}
                {remisionData.LaboratorioRemision?.Lr_Correo && <div style={{ fontSize: '11px', color: '#666' }}>Correo: {remisionData.LaboratorioRemision.Lr_Correo}</div>} */}
              </div>

              {/* Card Usuario */}
              <div
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: 'white',
                }}>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Creado por:</h5>
                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{remisionData.Usuario?.Us_Nombre || 'Usuario desconocido'}</div>
              </div>

              {/* Card Responsable */}
              {remisionData.Rm_ResponsableEnvio && (
                <div
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '12px',
                    backgroundColor: 'white',
                  }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Remisión recibida por:</h5>
                  <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{remisionData.Rm_ResponsableEnvio}</div>
                </div>
              )}

              {/* Card Temperaturas */}
              {(remisionData.Rm_TemperaturaNevera || remisionData.Rm_TemperaturaAmbiente) && (
                <div
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '12px',
                    backgroundColor: 'white',
                  }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Condiciones de Temperatura</h5>
                  {remisionData.Rm_TemperaturaNevera && (
                    <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                      <strong>Nevera:</strong> {remisionData.Rm_TemperaturaNevera}
                    </div>
                  )}
                  {remisionData.Rm_TemperaturaAmbiente && (
                    <div style={{ fontSize: '12px' }}>
                      <strong>Ambiente:</strong> {remisionData.Rm_TemperaturaAmbiente}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Notas generales */}
            {remisionData.Rm_Notas && (
              <div
                style={{
                  marginTop: '16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: '#fff8e1',
                }}>
                <h5 style={{ margin: '0 0 8px 0', color: '#f57c00', fontSize: '14px' }}>Observaciones:</h5>
                <div style={{ fontSize: '12px', lineHeight: '1.4' }}>{remisionData.Rm_Notas}</div>
              </div>
            )}
          </div>

          {/* Sección de estudios */}
          <div className='estudios-section' style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                padding: '10px 20px',
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#007acc',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              <h4 style={{ margin: 0, fontSize: '14px' }}>ESTUDIOS REMITIDOS</h4>
              <div
                style={{
                  backgroundColor: 'white',
                  color: '#007acc',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}>
                Total: {remisionData.DetalleRemisiones?.length || 0}
              </div>
            </div>

            {/* DataGrid de estudios */}
            <div style={{ flex: 1, padding: '0' }}>
              <DataGrid dataSource={remisionData.DetalleRemisiones || []} keyExpr='Rmd_Id' showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true} height='100%' columnAutoWidth={true} showColumnLines={true} allowColumnReordering={true} allowColumnResizing={true}>
                <SearchPanel visible={true} placeholder='Buscar estudios...' />
                <FilterRow visible={true} />
                <Paging enabled={true} pageSize={20} />

                <Toolbar>
                  <Item location='before'>
                    <div style={{ fontSize: '14px', color: '#666' }}>{remisionData.DetalleRemisiones?.length || 0} estudio(s) en esta remisión</div>
                  </Item>
                  <Item name='searchPanel' />
                </Toolbar>

                {/* Columnas del grid */}
                <Column caption='ORDEN' cellRender={renderOrden} width={90} alignment='center' />

                <Column caption='PACIENTE' cellRender={renderPaciente} minWidth={200} />

                <Column dataField='Estudio.Es_Codigo' caption='CÓDIGO' cellRender={renderEstudioCodigo} width={85} alignment='center' />

                <Column dataField='Estudio.Es_Nombre' caption='ESTUDIO' minWidth={200} cellRender={(cellData) => <div style={{ fontSize: '13px' }}>{cellData.value || 'Sin nombre'}</div>} />

                <Column caption='ÁREA' cellRender={renderArea} width={110} alignment='center' />

                <Column dataField='Rmd_Cantidad' caption='CANT.' cellRender={renderCantidad} width={60} alignment='center' />

                <Column dataField='Rmd_Urgente' caption='PRIORIDAD' cellRender={renderUrgente} width={100} alignment='center' />

                <Column dataField='Rmd_ResultadoRecibido' caption='RESULTADO' cellRender={renderResultadoRecibido} width={100} alignment='center' />

                <Column dataField='Rmd_Fecha' caption='FECHA REMISIÓN' dataType='datetime' format='dd/MM/yyyy HH:mm' width={140} alignment='center' />

                <Column dataField='Rmd_Nota' caption='OBSERVACIONES' cellRender={renderNotas} minWidth={150} />
              </DataGrid>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 20px',
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <div style={{ fontSize: '12px', color: '#666' }}>{remisionData.Rm_FechaModificacion && <span>Última modificación: {moment(remisionData.Rm_FechaModificacion).format('DD/MM/YYYY HH:mm')}</span>}</div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button text='Imprimir' type='normal' stylingMode='outlined' onClick={handleClicPrint} />
              <Button text='Cerrar' type='default' stylingMode='contained' onClick={handleClose} />
            </div>
          </div>
        </div>
      </Popup>
      <ModalReportRemision visible={visibleReport} setVisible={handleCloseModalPrint} reportName='Remisiones' idRemision={remisionId ? remisionId : -1} />
    </>
  );
};
